import { SearchIcon } from '@diligentcorp/atlas-react-icons';
import { useAtlasSemanticThemeTokens } from '@issuemanager/apps/client/theme';
import {
  DEBOUNCE_TIME,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import { Stack, debounce } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { ThreeDots } from '../../animations';
import { TextField, type TextFieldProps } from '../text-field';

export type SearchBoxProps = TextFieldProps & {
  onSearchTermChange: (value: string) => void;
};

export function SearchBox(props: SearchBoxProps) {
  const { onSearchTermChange, ...rest } = props;
  const [inputValue, setInputValue] = useState<string>('');
  const [isDebouncing, setIsDebouncing] = useState<boolean>(false);

  const semantic = useAtlasSemanticThemeTokens();

  const debouncedFunction = useMemo(
    () =>
      debounce((string: string) => {
        setIsDebouncing(false);
        onSearchTermChange(string);
      }, DEBOUNCE_TIME),
    [onSearchTermChange],
  );

  const handleChange = useCallback(
    (value: string) => {
      setInputValue(value);
      setIsDebouncing(true);
      debouncedFunction(value);
    },
    [debouncedFunction],
  );

  return (
    <Stack
      maxWidth={rest.fullWidth ? '100%' : 200}
      sx={{ backgroundColor: semantic.color.background.container.value }}
    >
      <TextField
        value={inputValue}
        onChange={(e) => handleChange(truncateSearchText(e.target.value))}
        InputProps={{
          startAdornment: <SearchIcon />,
          endAdornment: isDebouncing && <ThreeDots />,
          autoFocus: true,
          ...rest.InputProps,
        }}
        {...rest}
      />
    </Stack>
  );
}
